import {recordRumDurationVital} from '../datadogRum';
import {getDurationContractForMark} from './durationContracts';
import {
  type PerfMarkContext,
  type PerfMarkDetail,
  type PerformanceDurationContract,
} from './types';

type PerformanceDurationMarkDetail = Extract<
  PerfMarkDetail,
  {markType: 'duration'}
>;

// Needed because start and end marks arrive separately
type PendingStart = {
  // Prevents stale starts from matching later work
  expiresAt: number;
  // Keeps the start usable after trace cleanup
  mark: PerformanceMark;
};

// Prevents overlapping durations from sharing a start
const pendingStarts = new Map<string, PendingStart>();

// Caps Chrome DevTools Performance trace noise by name
const perfMarkCountsByName = new Map<string, number>();
const perfMeasureCountsByName = new Map<string, number>();

// Guards against abandoned starts growing forever
const MAX_PENDING_STARTS = 500;

// Keeps repeated trace entries inspectable
const MAX_PERFORMANCE_TIMELINE_ENTRIES_PER_NAME = 100;

/**
 * Processes registered perf marks
 * Single marks only need trace cleanup here
 * Duration marks can pair into RUM duration vitals
 */
export function processRegisteredPerfMark(mark: PerformanceMark) {
  const rawDetail = mark.detail;

  // Avoid turning unrelated marks into telemetry
  if (!getIsCustomPerfMarkDetail(rawDetail)) {
    return;
  }

  const detail = rawDetail;

  // Avoid flooding the trace with repeated mark entries
  trackAndTrimPerfEntriesByName(perfMarkCountsByName, mark.name, 'mark');

  if (detail.markType === 'single') {
    return;
  }

  // Avoid pairing stale starts with later work
  for (const [pendingKey, pending] of pendingStarts) {
    if (pending.expiresAt < mark.startTime) {
      pendingStarts.delete(pendingKey);
    }
  }

  const startContract = getDurationContractForMark(mark.name, 'start');
  if (startContract != null && hasRequiredOperationKey(startContract, detail)) {
    // Start and end marks can come from different components
    pendingStarts.set(pairKey(startContract, detail), {
      mark,
      expiresAt:
        mark.startTime + (startContract.maxPairingDurationMs ?? 60_000),
    });

    // Missing end marks should not grow memory forever
    for (const pendingKey of pendingStarts.keys()) {
      if (pendingStarts.size <= MAX_PENDING_STARTS) {
        break;
      }
      pendingStarts.delete(pendingKey);
    }
  }

  const endContract = getDurationContractForMark(mark.name, 'end');
  if (endContract != null && hasRequiredOperationKey(endContract, detail)) {
    // Missing starts were either superseded or expired
    const pendingKey = pairKey(endContract, detail);
    const start = pendingStarts.get(pendingKey);
    if (start == null) {
      return;
    }

    // Avoid emitting partial or broken durations
    const duration = mark.startTime - start.mark.startTime;
    pendingStarts.delete(pendingKey);
    if (duration < 0) {
      return;
    }

    const startDetail = start.mark.detail as PerformanceDurationMarkDetail;
    const durationContext = mergePerfMarkContexts(
      startDetail.context,
      detail.context
    );

    const operationKey = detail.operationKey ?? startDetail.operationKey;
    const devtoolsProperties: Array<[string, string]> =
      operationKey == null ? [] : [['operationKey', operationKey]];
    for (const [contextKey, contextValue] of Object.entries(durationContext)) {
      for (const [key, value] of Object.entries(contextValue)) {
        devtoolsProperties.push([`${contextKey}.${key}`, String(value)]);
      }
    }

    try {
      // For Chrome DevTools trace inspection, not RUM
      performance.measure(endContract.durationName, {
        start: start.mark.startTime,
        end: mark.startTime,
        detail: {
          durationContext,
          devtools: {
            dataType: 'track-entry',
            track: endContract.devtools.track,
            trackGroup: endContract.devtools.trackGroup,
            color: endContract.devtools.color,
            tooltipText:
              endContract.devtools.tooltipText ?? endContract.description,
            properties: devtoolsProperties,
          },
        },
      });

      // Avoid flooding the trace with repeated duration entries
      trackAndTrimPerfEntriesByName(
        perfMeasureCountsByName,
        endContract.durationName,
        'measure'
      );
    } catch {
      // RUM should not depend on local trace support
    }

    // Datadog expects epoch milliseconds
    recordRumDurationVital(endContract.durationName, {
      startTime: performance.timeOrigin + start.mark.startTime,
      duration,
      description: endContract.description,
      context: durationContext,
    });
  }
}

function mergePerfMarkContexts(
  startContext: PerfMarkContext,
  endContext: PerfMarkContext
): PerfMarkContext {
  // Merge within each context group so start-side pain attributes such as
  // search.inputSource survive alongside end-side attributes for the same vital.
  const durationContext: PerfMarkContext = {...startContext};
  for (const [key, endValue] of Object.entries(endContext)) {
    durationContext[key] = {
      ...durationContext[key],
      ...endValue,
    };
  }
  return durationContext;
}

function getIsCustomPerfMarkDetail(
  rawDetail: unknown
): rawDetail is PerfMarkDetail {
  if (typeof rawDetail !== 'object' || rawDetail == null) {
    return false;
  }

  const detail = rawDetail as {
    __wandbPerformanceMark?: unknown;
    context?: unknown;
    markType?: unknown;
    operationKey?: unknown;
    scopeStartTime?: unknown;
  };

  if (
    detail.__wandbPerformanceMark !== true ||
    typeof detail.context !== 'object' ||
    detail.context == null ||
    (detail.operationKey != null && typeof detail.operationKey !== 'string')
  ) {
    return false;
  }

  if (detail.markType === 'single') {
    // Single marks are custom but never pair into durations
    return true;
  }

  return (
    detail.markType === 'duration' && typeof detail.scopeStartTime === 'number'
  );
}

/**
 * Prevents stale pairs after observer resets
 */
export function clearPendingMarks() {
  pendingStarts.clear();
  perfMarkCountsByName.clear();
  perfMeasureCountsByName.clear();
}

function pairKey(
  contract: PerformanceDurationContract,
  detail: PerformanceDurationMarkDetail
) {
  // Prevents overlap across scopes, contracts, and operations
  return `${detail.scopeStartTime}:${contract.durationName}:${
    detail.operationKey ?? 'single'
  }`;
}

function hasRequiredOperationKey(
  contract: PerformanceDurationContract,
  detail: PerformanceDurationMarkDetail
) {
  // Required by default because two overlapping operations can share a timing
  // scope and duration contract; without a key, an end mark could pair with the
  // wrong start and emit a misleading duration.
  // Only safe when the duration cannot overlap
  return contract.requireOperationKey === false || detail.operationKey != null;
}

/**
 * Prevents repeated marks/measures from crowding Chrome DevTools Perf traces
 *
 * Official docs:
 * https://www.w3.org/TR/user-timing/#dom-performance-clearmarks
 * https://www.w3.org/TR/user-timing/#dom-performance-clearmeasures
 */
function trackAndTrimPerfEntriesByName(
  countsByName: Map<string, number>,
  name: string,
  entryType: 'mark' | 'measure'
) {
  const nextCount = (countsByName.get(name) ?? 0) + 1;
  if (nextCount < MAX_PERFORMANCE_TIMELINE_ENTRIES_PER_NAME) {
    countsByName.set(name, nextCount);
    return;
  }

  try {
    // Clear by name so recent trace entries are easier to inspect
    if (entryType === 'mark') {
      // Safe for RUM because pairing uses stored start/end times
      performance.clearMarks(name);
    } else {
      performance.clearMeasures(name);
    }
    countsByName.set(name, 0);
  } catch {
    countsByName.set(name, nextCount);
  }
}
